import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { User } from "@supabase/supabase-js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Settings, Award, LogOut, Menu, Users, MessageSquare, BookOpen, Video } from "lucide-react";
import maipLogo from "@/assets/maip-logo.png";
import { BedarfsAnalysatorDialog } from "@/components/BedarfsAnalysatorDialog";

const Header = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isExpert, setIsExpert] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBedarfsAnalysator, setShowBedarfsAnalysator] = useState(false);
  const navigate = useNavigate();

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    // Check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        checkExpertRole(session.user.id);
        checkAdminRole(session.user.id);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
        if (session?.user) {
          checkExpertRole(session.user.id);
          checkAdminRole(session.user.id);
        } else {
          setIsExpert(false);
          setIsAdmin(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const checkExpertRole = async (userId: string) => {
    try {
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .in("role", ["experte", "admin"]);

      setIsExpert(roles && roles.length > 0);
    } catch (error) {
      console.error("Error checking expert role:", error);
      setIsExpert(false);
    }
  };

  const checkAdminRole = async (userId: string) => {
    try {
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("role", "admin");

      setIsAdmin(roles && roles.length > 0);
    } catch (error) {
      console.error("Error checking admin role:", error);
      setIsAdmin(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src={maipLogo} alt="MAIP.info Logo" className="h-[40px]" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6 flex-1 justify-center">
            {/* Über MAIP Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-foreground hover:text-primary transition-colors font-medium">
                  Über MAIP
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 bg-background z-50">
                <DropdownMenuItem asChild>
                  <Link to="/warum-maip" className="cursor-pointer">
                    Warum MAIP?
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/experten" className="cursor-pointer">
                    Dozierende
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/gestaltungsrat" className="cursor-pointer">
                    Gestaltungsrat
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Programm & Module Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-foreground hover:text-primary transition-colors font-medium">
                  Programm & Module
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 bg-background z-50">
                <DropdownMenuItem asChild>
                  <Link to="/programm" className="cursor-pointer">
                    Gesamtprogramm
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/grundlagen" className="cursor-pointer">
                    KI-Grundlagen
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/datenschutz-kompass" className="cursor-pointer">
                    Datenschutz-Kompass
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/mitbestimmung-info" className="cursor-pointer">
                    KI & Mitbestimmung
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <button 
                    onClick={() => setShowBedarfsAnalysator(true)}
                    className="cursor-pointer w-full flex items-center text-primary font-semibold"
                  >
                    Welches Modul passt zu mir?
                  </button>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Wissen & Praxis Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-foreground hover:text-primary transition-colors font-medium">
                  Wissen & Praxis
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 bg-background z-50">
                <DropdownMenuItem asChild>
                  <Link to="/wissen" className="cursor-pointer">
                    Fachartikel
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/glossar" className="cursor-pointer">
                    KI-Glossar
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/ideenboard" className="cursor-pointer">
                    Ideen- & Lösungsboard
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Preise & Förderung (Direct Link) */}
            <Link 
              to="/preise"
              className="text-foreground hover:text-primary transition-colors font-medium"
            >
              Preise & Förderung
            </Link>

            {/* Community Dropdown - Only for Experts */}
            {isExpert && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="text-foreground hover:text-primary transition-colors font-medium flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    Community
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-background z-50">
                  <DropdownMenuItem asChild>
                    <Link to="/experten-bereich/forum" className="cursor-pointer flex items-center">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Forum
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/experten-bereich/ressourcen" className="cursor-pointer flex items-center">
                      <BookOpen className="w-4 h-4 mr-2" />
                      Ressourcen
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/experten-bereich/webinare" className="cursor-pointer flex items-center">
                      <Video className="w-4 h-4 mr-2" />
                      Webinare
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/experten-bereich" className="cursor-pointer flex items-center">
                      <Award className="w-4 h-4 mr-2" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </nav>

          {/* Secondary Links (FAQ & Kontakt) */}
          <div className="hidden md:flex items-center gap-4 ml-4">
            <Link 
              to="/faq"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              FAQ
            </Link>
            <Link 
              to="/kontakt"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Kontakt
            </Link>
          </div>

          {/* Desktop User Menu - Only for Admins */}
          <div className="hidden md:flex ml-4 items-center gap-3">
            {isAdmin ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Settings className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-background z-50">
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="cursor-pointer flex items-center">
                      <Settings className="w-4 h-4 mr-2" />
                      Admin-Bereich
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/experten-bereich" className="cursor-pointer flex items-center">
                      <Award className="w-4 h-4 mr-2" />
                      Mein Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/admin/content" className="cursor-pointer flex items-center">
                      <Settings className="w-4 h-4 mr-2" />
                      Inhalte verwalten
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : user && isExpert ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Settings className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-background z-50">
                  <DropdownMenuItem asChild>
                    <Link to="/experten-bereich" className="cursor-pointer flex items-center">
                      <Award className="w-4 h-4 mr-2" />
                      Mein Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-3">
            {isAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Settings className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-background z-50">
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="cursor-pointer flex items-center">
                      <Settings className="w-4 h-4 mr-2" />
                      Admin-Bereich
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/experten-bereich" className="cursor-pointer flex items-center">
                      <Award className="w-4 h-4 mr-2" />
                      Mein Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/admin/content" className="cursor-pointer flex items-center">
                      <Settings className="w-4 h-4 mr-2" />
                      Inhalte verwalten
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px]">
                <SheetHeader>
                  <SheetTitle>Navigation</SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col gap-4 mt-8">
                  {/* Über MAIP Section */}
                  <div className="pt-2 pb-2 text-sm font-semibold text-muted-foreground border-t">
                    Über MAIP
                  </div>
                  <Link 
                    to="/warum-maip"
                    className="text-foreground hover:text-primary transition-colors font-medium py-2 pl-4"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Warum MAIP?
                  </Link>
                  <Link 
                    to="/experten"
                    className="text-foreground hover:text-primary transition-colors font-medium py-2 pl-4"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dozierende
                  </Link>
                  <Link 
                    to="/gestaltungsrat"
                    className="text-foreground hover:text-primary transition-colors font-medium py-2 pl-4"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Gestaltungsrat
                  </Link>

                  {/* Programm & Module Section */}
                  <div className="pt-4 pb-2 text-sm font-semibold text-muted-foreground border-t">
                    Programm & Module
                  </div>
                  <Link 
                    to="/programm"
                    className="text-foreground hover:text-primary transition-colors font-medium py-2 pl-4"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Gesamtprogramm
                  </Link>
                  <Link 
                    to="/grundlagen"
                    className="text-foreground hover:text-primary transition-colors font-medium py-2 pl-4"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    KI-Grundlagen
                  </Link>
                  <Link 
                    to="/datenschutz-kompass"
                    className="text-foreground hover:text-primary transition-colors font-medium py-2 pl-4"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Datenschutz-Kompass
                  </Link>
                  <Link 
                    to="/mitbestimmung-info"
                    className="text-foreground hover:text-primary transition-colors font-medium py-2 pl-4"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    KI & Mitbestimmung
                  </Link>
                  <button
                    onClick={() => {
                      setShowBedarfsAnalysator(true);
                      setMobileMenuOpen(false);
                    }}
                    className="text-primary hover:text-primary/80 transition-colors font-bold py-2 pl-4 text-left w-full"
                  >
                    Welches Modul passt zu mir?
                  </button>

                  {/* Wissen & Praxis Section */}
                  <div className="pt-4 pb-2 text-sm font-semibold text-muted-foreground border-t">
                    Wissen & Praxis
                  </div>
                  <Link 
                    to="/wissen"
                    className="text-foreground hover:text-primary transition-colors font-medium py-2 pl-4"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Fachartikel
                  </Link>
                  <Link 
                    to="/glossar"
                    className="text-foreground hover:text-primary transition-colors font-medium py-2 pl-4"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    KI-Glossar
                  </Link>
                  <Link 
                    to="/ideenboard"
                    className="text-foreground hover:text-primary transition-colors font-medium py-2 pl-4"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Ideen- & Lösungsboard
                  </Link>

                  {/* Preise & Förderung */}
                  <Link 
                    to="/preise"
                    className="text-foreground hover:text-primary transition-colors font-medium py-2 border-t pt-4"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Preise & Förderung
                  </Link>

                  {/* Secondary Links */}
                  <div className="pt-4 pb-2 text-sm font-semibold text-muted-foreground border-t">
                    Service
                  </div>
                  <Link 
                    to="/faq"
                    className="text-foreground hover:text-primary transition-colors py-2 pl-4"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    FAQ
                  </Link>
                  <Link 
                    to="/kontakt"
                    className="text-foreground hover:text-primary transition-colors py-2 pl-4"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Kontakt
                  </Link>
                  
                  {/* Community Section - Only for Experts */}
                  {isExpert && (
                    <>
                      <div className="pt-4 pb-2 text-sm font-semibold text-muted-foreground border-t">
                        Community
                      </div>
                      <Link 
                        to="/experten-bereich/forum"
                        className="text-foreground hover:text-primary transition-colors font-medium py-2 pl-4 flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <MessageSquare className="w-4 h-4" />
                        Forum
                      </Link>
                      <Link 
                        to="/experten-bereich/ressourcen"
                        className="text-foreground hover:text-primary transition-colors font-medium py-2 pl-4 flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <BookOpen className="w-4 h-4" />
                        Ressourcen
                      </Link>
                      <Link 
                        to="/experten-bereich/webinare"
                        className="text-foreground hover:text-primary transition-colors font-medium py-2 pl-4 flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Video className="w-4 h-4" />
                        Webinare
                      </Link>
                      <Link 
                        to="/experten-bereich"
                        className="text-foreground hover:text-primary transition-colors font-medium py-2 pl-4 flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Award className="w-4 h-4" />
                        Dashboard
                      </Link>
                    </>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      <BedarfsAnalysatorDialog 
        open={showBedarfsAnalysator} 
        onOpenChange={setShowBedarfsAnalysator} 
      />
    </header>
  );
};

export default Header;
